import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "store";
import SearchCriteria from "models/TransferModels/SearchCriteria";

export function useSearchCriteriaFromQuery(): SearchCriteria | undefined {
    const { search } = useLocation();
    const { budgets, stayTypes, durations, transports, countries } = useSelector((s: RootState) => s.globalSlice);

    const parsed = useMemo(() => new URLSearchParams(search), [search]);
    const [criteria, setCriteria] = useState<SearchCriteria | undefined>(undefined);

    useEffect(() => {
        if (
            budgets.length === 0 ||
            stayTypes.length === 0 ||
            durations.length === 0 ||
            transports.length === 0 ||
            countries.length === 0
        ) return;

        try {
            const readIds = (key: string): number[] => {
                const raw = parsed.get(key);
                if (!raw) return [];
                return raw.split(",").map((n) => parseInt(n, 10)).filter((n) => !Number.isNaN(n));
            };


            const readStrings = (key: string): string[] => {
                const all = parsed.getAll(key).flatMap((v) => (v ? v.split(",") : []));
                return all
                    .map((s) => s.trim())
                    .filter((s) => s.length > 0);
            };

            const destinationId = parsed.get("destinationId");
            const destinationsIds = parsed.get("destinationsIds");

            const destIds: number[] = destinationsIds
                ? readIds("destinationsIds")
                : destinationId
                    ? [parseInt(destinationId, 10)].filter((n) => !Number.isNaN(n))
                    : [];

            const continents = readStrings("continents");

            const stayIds = readIds("stayTypes");
            const durIds = readIds("durations");
            const budIds = readIds("budgets");
            const traIds = readIds("transports");

            const from = parsed.get("from") ? new Date(parsed.get("from") as string) : undefined as any;
            const to = parsed.get("to") ? new Date(parsed.get("to") as string) : undefined as any;

            const resolved: SearchCriteria = {
                destinations: countries.filter((c) => destIds.includes(c.id)),
                continents,
                stayTypes: stayTypes.filter((s) => stayIds.includes(s.id)),
                durations: durations.filter((d) => durIds.includes(d.id)),
                budgets: budgets.filter((b) => budIds.includes(b.id)),
                transports: transports.filter((t) => traIds.includes(t.id)),
                from,
                to,
            };

            const hasAny =
                resolved.destinations.length > 0 ||
                resolved.continents.length > 0 ||
                resolved.stayTypes.length > 0 ||
                resolved.durations.length > 0 ||
                resolved.budgets.length > 0 ||
                resolved.transports.length > 0 ||
                (!!from && !!to);

            setCriteria(hasAny ? resolved : undefined);
        } catch {
            setCriteria(undefined);
        }
    }, [budgets, stayTypes, durations, transports, countries, parsed]);

    return criteria;
}